import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ChangeDetectorRef,
} from '@angular/core';
import { take } from 'rxjs';

import { Workspace } from '@digital-platform/reporting/domain';
import { ReportingService } from '@digital-platform/reporting/services';
import {
  CategoryListNavComponent,
  Dropdown,
  Item,
  NavData,
  NavSettings,
} from '@digital-platform/ui-base-components/category-list-nav';
/**
 * 1. pull in data access
 * 2. build data transform functions
 * 3. bring in settings
 * 4. wire hooks into shell ( if necessary)
 */

@Component({
  selector: 'digital-platform-report-tracker',
  standalone: true,
  imports: [CommonModule, CategoryListNavComponent],
  templateUrl: './report-tracker.component.html',
  styleUrls: ['./report-tracker.component.scss'],
})
export class ReportTrackerComponent implements OnChanges {
  private readonly reportingService = inject(ReportingService);
  private readonly cdr = inject(ChangeDetectorRef);

  @Input() workspaces: Workspace[] = [];
  @Input() data: NavData = [] as NavData;
  @Input() settings: NavSettings = {} as NavSettings;
  @Input() embeddedPageMode = false;

  private _favorites: Item[] = [];
  @Input()
  get favorites(): Item[] {
    return this._favorites;
  }
  set favorites(value: Item[]) {
    this._favorites = value;
    this.cdr.detectChanges(); // Ensure change detection is triggered
  }

  @Output() itemClick = new EventEmitter<Item>();
  @Output() itemInfoClick = new EventEmitter<Item>();
  @Output() dropdownClick = new EventEmitter<Dropdown>();
  @Output() favoriteItemRemoved = new EventEmitter<Item>();
  @Output() favoritesChanged = new EventEmitter<Item>();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.['favorites']?.currentValue) {
      this.favorites = changes['favorites'].currentValue;
    }

    if (changes?.['workspaces']?.currentValue) {
      this.initializeComponent(
        changes['workspaces'].currentValue as Workspace[]
      );
    }
  }

  /**
   * Init props that need refresh on change
   * @param workspaces
   */
  initializeComponent(workspaces: Workspace[]): void {
    this.reportingService
      .getCategorizedReports(workspaces)
      .pipe(take(1))
      .subscribe((data) => {
        this.data = data;
        this.settings = this.getNavSettings();
        this.cdr.detectChanges(); // Ensure change detection is triggered
      });
  }

  /**
   * Store nav component settings
   * @returns
   */
  getNavSettings(): NavSettings {
    return {
      title: 'Reports',
      subtitle: 'REPORT TRACKER',
      items: {
        showInfo: true,
      },
      dropdowns: {
        showCount: true,
        allItems: {
          show: true,
          defaultOutput: true,
          name: 'All Items',
          id: 'all-items',
          link: undefined,
          styles: {
            hoverCountColor: '#D3D3D3',
            activeColor: '#EDEDED',
            color: '#2A2A2B',
            iconName: 'all-items',
          },
        },
        favorites: {
          show: true,
          name: 'Favorites',
          id: 'favorites',
          link: undefined,
          styles: {
            hoverCountColor: '#B2E2F7',
            activeColor: '#E6F6FC',
            color: '#0472A2',
            iconName: 'favorites',
          },
        },
      },
    };
  }

  handleItemClick(item: Item) {
    this.itemClick.emit(item);
  }
  handleItemInfoClick(item: Item) {
    this.itemInfoClick.emit(item);
  }
  handleDropdownClick(dropdown: Dropdown) {
    this.dropdownClick.emit(dropdown);
  }
  handleFavoriteItemRemoved(item: Item) {
    this.favoriteItemRemoved.emit(item);
  }
  handleFavoritesChanged(item: Item) {
    this.favoritesChanged.emit(item);
  }
  addFavoriteItem(item: Item) {
    this.favorites.push(item);
    this.cdr.detectChanges(); // Ensure change detection is triggered
  }
}
